import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { siteRoute } from '@/lib/domains';
import { SmartLink } from '@/components/shared/SmartLink';

const NAV_LINKS = [
  { label: 'Home',       to: '/' },
  { label: 'The App',    to: '/the-app' },
  { label: 'Safety',     to: '/safety' },
  { label: 'Membership', to: '/membership' },
  { label: 'Access',     to: '/access' },
];

const slug = (s) => s.toLowerCase().replace(/\s+/g, '-');

export const Navigation = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close the mobile menu on any route change
  useEffect(() => { setMenuOpen(false); }, [location.pathname]);

  // Lock body scroll while mobile menu is open
  useEffect(() => {
    if (!menuOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [menuOpen]);

  const isActive = (to) =>
    to === '/' ? location.pathname === '/' : location.pathname.startsWith(to);

  return (
    <>
      <nav
        className={`site-nav ${scrolled || menuOpen ? 'nav-scrolled' : ''}`}
        data-testid="main-navigation"
      >
        <div className="nav-inner">
          <Link to="/" className="nav-logo" data-testid="nav-logo">NYMPHÉ</Link>

          <div className="nav-links-desktop" data-testid="nav-links-desktop">
            {NAV_LINKS.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                className={`nav-link${isActive(l.to) ? ' active' : ''}`}
                data-testid={`nav-link-${slug(l.label)}`}
              >
                {l.label}
              </Link>
            ))}
          </div>

          <SmartLink
            to={siteRoute('/access')}
            className="nav-cta-desktop"
            data-testid="nav-cta-desktop"
          >
            Request Early Access
          </SmartLink>

          <button
            type="button"
            className="nav-menu-btn"
            onClick={() => setMenuOpen((o) => !o)}
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={menuOpen}
            data-testid="nav-menu-btn"
          >
            {menuOpen ? (
              <svg width="20" height="20" viewBox="0 0 20 20" aria-hidden="true">
                <line x1="4"  y1="4"  x2="16" y2="16" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
                <line x1="16" y1="4"  x2="4"  y2="16" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
              </svg>
            ) : (
              <svg width="20" height="20" viewBox="0 0 20 20" aria-hidden="true">
                <line x1="3" y1="7"  x2="17" y2="7"  stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
                <line x1="3" y1="13" x2="17" y2="13" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
              </svg>
            )}
          </button>
        </div>
      </nav>

      {menuOpen && (
        <div
          className="nav-mobile-overlay"
          data-testid="nav-mobile-overlay"
          role="dialog"
          aria-modal="true"
          onClick={(e) => { if (e.target === e.currentTarget) setMenuOpen(false); }}
        >
          <div className="nav-mobile-content">
            {NAV_LINKS.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                className={`nav-mobile-link${isActive(l.to) ? ' active' : ''}`}
                onClick={() => setMenuOpen(false)}
                data-testid={`nav-mobile-link-${slug(l.label)}`}
              >
                {l.label}
              </Link>
            ))}
            <SmartLink
              to={siteRoute('/access')}
              className="nav-mobile-cta"
              onClick={() => setMenuOpen(false)}
              data-testid="nav-mobile-cta"
            >
              Request Early Access
            </SmartLink>
          </div>
        </div>
      )}

      {/* ─── Mobile bottom nav — visible only on mobile (CSS-gated).
            Replaces the hamburger as primary navigation: the five main
            routes stay visible at all times while scrolling, app-like
            and editorial. ───────────────────────────────────────────── */}
      <nav
        className="site-nav-bottom"
        data-testid="mobile-bottom-nav"
        aria-label="Primary"
      >
        {NAV_LINKS.map((l) => (
          <Link
            key={l.to}
            to={l.to}
            className={`site-nav-bottom-link${isActive(l.to) ? ' active' : ''}`}
            data-testid={`mobile-bottom-link-${slug(l.label)}`}
          >
            <span className="site-nav-bottom-label">{l.label}</span>
          </Link>
        ))}
      </nav>
    </>
  );
};

export default Navigation;
